import { FormEvent, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import toast, { Toaster } from "react-hot-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover";
import { PopoverClose } from "@radix-ui/react-popover";
import ConsoleTable, { DataListProps } from "../-components/ConsoleTable";
import Pagination from "components/Pagination";
import { makeIntComma, RouteTypes, toastErrorSyle } from "hooks/utils/util";
import PageHeader from "../-components/PageHeader";
import { useGetConsoleQrSystem } from "hooks/console/useGetConsoleQrSystem";
import { usePutConsoleQrSystem } from "hooks/console/usePutConsoleQrSystem";
import ConfirmDialog from "components/popup/ConfirmDialog";
import { useDeleteConsoleQrSystem } from "hooks/console/useDeleteConsoleQrSystem";
import Button from "common/pages/morning/-components/common/Button";
import { usePostConsoleQrRequestComment } from "hooks/console/usePostConsoleQrRequestComment";
import { ReactComponent as ArrowDown } from "assets/morning/icon/arrowDownNavy.svg";
import { ReactComponent as Memo } from "assets/morning/icon/memo.svg";
import { ReactComponent as Close } from "assets/morning/icon/close.svg";
import { ReactComponent as MemoOn } from "assets/morning/icon/memoOn.svg";
import { ReactComponent as ReadingGlasses } from "assets/common/common/readingGlasses.svg";
import { useGetConsoleQrSystemDetail } from "hooks/console/useGetConsoleQrSystemDetail";
import Seo from "components/Seo";
// import DayPickerInput from "common/pages/morning/-components/common/DayPickerInput";
import { useGetConsoleQrSystemStatus } from "hooks/console/useGetConsoleQrSystemStatus";
import LoadingPop from "components/popup/LoadingPop";

type SearchFormProps = {
    from_date: Date | undefined;
    to_date: Date | undefined;
    page: number;
    limit: string;
    searchtxt: string;
};

const theadStyle = "text-[#222222] text-[16px] text-center";

const initialSearchForm = {
    from_date: undefined,
    to_date: undefined,
    page: 1,
    limit: "10",
    searchtxt: "",
};

export default function ConsoleQrRequest({ type }: { type: RouteTypes }) {
    const navigate = useNavigate();
    const location = useLocation();
    const now = new Date();
    const currentMonth = `${now.getMonth() + 1}`.padStart(2, "0");
    const [memo, setMemo] = useState("");
    const [month, setMonth] = useState(currentMonth);
    const [submittedValue, setSubmittedValue] = useState<string | null>(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const [selectedRows, setSelectedRows] = useState<number[]>([]);
    const [searchForm, setSearchForm] = useState<SearchFormProps>(initialSearchForm);
    const [selectedIdx, setSelectedIdx] = useState<string | null>(null);
    const allCheckRef = useRef<HTMLInputElement | null>(null);
    const memoRef = useRef<HTMLButtonElement | null>(null);

    const { data, refetch, isLoading } = useGetConsoleQrSystem(searchForm);
    const { mutate: update } = usePutConsoleQrSystem();
    const { mutate: deleteRequest } = useDeleteConsoleQrSystem();
    const { mutate: postMemo } = usePostConsoleQrRequestComment();
    const { data: detailData, isLoading: isLoadingDetail } = useGetConsoleQrSystemDetail({ list_no: selectedIdx ?? "" });
    const { data: statusData, isLoading: isLoadingStatus } = useGetConsoleQrSystemStatus({ month: submittedValue });

    // 사이드바 링크 재 클릭시 상태 초기화
    useEffect(() => {
        if (location.state?.reset) {
            // 초기화 로직 수행
            setSearchForm(initialSearchForm);
        }
    }, [location.state]);

    useEffect(() => {
        const page = searchParams.get("page") ?? "1";
        if (page) {
            setSearchForm(prev => ({ ...prev, page: Number(page) }));
        }
    }, [searchParams]);

    const initialDateForStatus = () => {
        const year = now.getFullYear();
        const fullDate = `${year}-${currentMonth}`;
        return fullDate;
    };
    // 상태 테이블 초기값 (현재 년-월) 추가
    useEffect(() => {
        setSubmittedValue(initialDateForStatus());
    }, []);

    const deleteRow = () => {
        deleteRequest(
            { list_no: selectedRows.map(Number) },
            {
                onSuccess: () => {
                    toast.success("성공적으로 삭제가 되었습니다.");
                    refetch();
                },
                onError: (error: any) => {
                    const message = error.message;
                    toast.error(message, toastErrorSyle);
                },
            },
        );
    };

    const toggleRow = (id: number) => {
        const allIdxs = data?.data_list.map(item => item.list_no);

        setSelectedRows(prev => {
            if (prev.includes(id)) {
                // 이미 선택된 경우 제거
                const newRows = prev.filter(rowId => rowId !== id);
                if (newRows.length !== allIdxs?.length) allCheckRef.current?.click();
                return newRows;
            } else {
                // 없으면 추가
                const newRows = [...prev, id];
                if (newRows.length === allIdxs?.length) allCheckRef.current?.click();
                return newRows;
            }
        });
    };

    const allCheck = (checked: boolean) => {
        const allIdxs = data?.data_list.map(item => item.list_no);

        if (checked) {
            if (allIdxs) setSelectedRows(allIdxs);
        } else {
            setSelectedRows([]);
        }
    };

    function handleChangeRowStatus(row: DataListProps, value: string) {
        update(
            {
                list_no: row.list_no,
                result: value,
            },
            {
                onSuccess: () => {
                    toast.success("성공적으로 수정 되었습니다.");
                    refetch();
                },
                onError: (error: any) => {
                    const message = error.message;
                    toast.error(message, toastErrorSyle);
                },
            },
        );
    }

    // 메모 등록
    const handlePostMomo = (idx: string) => {
        if (!memo.trim()) {
            toast.error("내용을 입력해주세요.", toastErrorSyle);
            return;
        }
        postMemo(
            {
                list_no: idx,
                c_content: memo,
            },
            {
                onSuccess: () => {
                    toast.success("성공적으로 등록 되었습니다.");
                    memoRef.current?.click();
                },
                onError: (error: any) => {
                    const message = error.message;
                    toast.error(message, toastErrorSyle);
                },
            },
        );
    };

    const resetStatusForm = () => {
        // setSearchParams({});
        // setSearchForm(initialSearchForm);
        setMonth(currentMonth);
        setSubmittedValue(initialDateForStatus());
    };

    // 검색 버튼 클릭시
    const onSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!month) {
            toast.error("접수 월을 선택해주세요.", toastErrorSyle);
            return;
        }

        const year = new Date().getFullYear();
        const fullDate = `${year}-${month}`;
        setSubmittedValue(fullDate);

        // searchParams.set("month", String(newPage));
        // setSearchParams(searchParams);
    };

    const statusKeys = statusData ? Object.keys(statusData) : [];
    const dayKeys = statusData && statusKeys.length > 0 ? Object.keys(statusData[statusKeys[0]]) : [];

    return (
        <>
            <Seo type="console" pageTitle="QR시스템 사용 코드 변경 신청" />
            <div className="flex-1 flex flex-col overflow-hidden max-h-[100dvh]">
                <PageHeader
                    category="고객서비스"
                    title="QR시스템 사용 코드 변경 신청"
                    // linkTo="http://morning.ilshingroup.com/customer/?p_url=customer_7"
                />
                <div className="flex-1 bg-[#F7F6FB] flex overflow-hidden">
                    <div className="px-[40px] py-[28px] w-full overflow-y-auto">
                        <div className="bg-white rounded-[20px] py-[24px] px-[40px] w-full min-w-[900px] mb-10">
                            <form onSubmit={onSubmit} className="flex flex-col items-center gap-[20px]">
                                <div className="bg-[#F7F7F8] flex p-[20px] items-center w-full border-t border-t-[#E1E2E4]">
                                    <label htmlFor="" className="text-[20px] text-[#222222] font-[600] text-nowrap w-full max-w-[160px]">
                                        접수 월
                                    </label>

                                    {/* <div className="flex gap-2 items-center">
                                        <div className="flex gap-3 relative bg-white h-[52px] px-3 border border-[#E1E2E4] rounded-lg">
                                            <DayPickerInput
                                                initialDate={searchForm.from_date}
                                                twoCalendar
                                                onDateChange={value => setSearchForm(prev => ({ ...prev, from_date: value }))}
                                                maxDate={searchForm.to_date}
                                            />
                                            <DayPickerInput
                                                initialDate={searchForm.to_date}
                                                twoCalendar
                                                onDateChange={value => setSearchForm(prev => ({ ...prev, to_date: value }))}
                                                minDate={searchForm.from_date}
                                            />
                                        </div>
                                    </div> */}

                                    <Select name="month" value={month} onValueChange={value => setMonth(value)}>
                                        <SelectTrigger
                                            className={`relative shadow-none text-[#1B1C1E] text-[16px] flex gap-[10px] rounded-[5px] focus:ring-2 max-w-[120px] border border-[#E1E2E4] bg-white h-[50px]`}
                                        >
                                            <SelectValue placeholder="선택" />
                                            <ArrowDown className="[&_path]:fill-[#666666]" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-white">
                                            {Array.from({ length: 12 }, (_, i) => {
                                                const value = String(i + 1).padStart(2, "0");
                                                return (
                                                    <SelectItem key={value} value={value}>
                                                        {i + 1}
                                                    </SelectItem>
                                                );
                                            })}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="flex items-center gap-[8px]">
                                    <button
                                        type="button"
                                        className="bg-white border border-[#E1E2E4] rounded-lg text-[18px] text-[#878A93] w-[195px] py-[14px]"
                                        onClick={resetStatusForm}
                                    >
                                        검색 초기화
                                    </button>
                                    <button className="bg-[#132E47] border border-[#E1E2E4] rounded-lg text-[18px] text-white w-[195px] py-[14px]">
                                        검색
                                    </button>
                                </div>
                            </form>

                            {isLoadingStatus && <LoadingPop />}

                            {statusData && (
                                <figure className="overflow-x-auto mt-10">
                                    <table className="min-w-[800px] w-full border-collapse border border-gray-300 text-center text-sm">
                                        <thead>
                                            <tr className="bg-[#FBFBFC]">
                                                <th className="border p-5 bg-[#FBFBFC] sticky top-0 left-0 z-10">-</th>
                                                {dayKeys.map(day => (
                                                    <th key={day} className="border p-5 text-nowrap">
                                                        {day}
                                                    </th>
                                                ))}
                                                <th className="border p-5 text-nowrap bg-yellow-50">합계</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {statusKeys.map(status => {
                                                const total = dayKeys.reduce((sum, day) => sum + (statusData[status][day] ?? 0), 0); // ✅ 총합 계산
                                                return (
                                                    <tr key={status}>
                                                        <th className="border p-3 font-medium text-nowrap bg-white sticky left-0 z-10">
                                                            {status}
                                                        </th>
                                                        {dayKeys.map(day => (
                                                            <td key={day} className="border p-3">
                                                                {statusData[status][day] ?? 0}
                                                            </td>
                                                        ))}
                                                        <td className="border p-3 font-semibold bg-yellow-50">{total}</td>
                                                    </tr>
                                                );
                                            })}
                                            {/* ✅ 일별 합계 행 추가 */}
                                            <tr className="bg-[#FDF7E2] font-semibold">
                                                <th className="border p-3 text-nowrap sticky left-0 bg-[#FDF7E2] z-10">일별 합계</th>
                                                {dayKeys.map(day => {
                                                    const dailyTotal = statusKeys.reduce(
                                                        (sum, status) => sum + (statusData[status][day] ?? 0),
                                                        0,
                                                    );
                                                    return (
                                                        <td key={day} className="border p-3">
                                                            {dailyTotal}
                                                        </td>
                                                    );
                                                })}
                                                <td className="border p-3 bg-yellow-100">
                                                    {
                                                        // 전체 총합
                                                        statusKeys.reduce((sum, status) => {
                                                            return (
                                                                sum +
                                                                dayKeys.reduce((daySum, day) => {
                                                                    return daySum + (statusData[status][day] ?? 0);
                                                                }, 0)
                                                            );
                                                        }, 0)
                                                    }
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </figure>
                            )}
                        </div>

                        <div className="bg-white rounded-[20px] py-[36px] px-[40px] w-full min-w-[900px]">
                            <form>
                                <div className="flex items-center gap-2 justify-end">
                                    <Select
                                        value={searchForm.limit}
                                        onValueChange={value => setSearchForm(prev => ({ ...prev, limit: value }))}
                                    >
                                        <SelectTrigger
                                            className={`relative shadow-none text-[#1B1C1E] text-[16px] flex gap-[10px] rounded-[5px] focus:ring-2 max-w-[150px] border border-[#E1E2E4] bg-white h-[50px]`}
                                        >
                                            <SelectValue placeholder="" />
                                            <ArrowDown className="[&_path]:fill-[#666666]" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-white">
                                            <SelectItem value="20">20개</SelectItem>
                                            <SelectItem value="10">10개</SelectItem>
                                        </SelectContent>
                                    </Select>

                                    <Select
                                        value="comany"
                                        //  onValueChange={value => setSearchForm(prev => ({ ...prev, search: value }))}
                                    >
                                        <SelectTrigger
                                            className={`relative shadow-none text-[#1B1C1E] text-[16px] flex gap-[10px] rounded-[5px] focus:ring-2 max-w-[120px] border border-[#E1E2E4] bg-white h-[50px]`}
                                        >
                                            <SelectValue placeholder="" />
                                            <ArrowDown className="[&_path]:fill-[#666666]" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-white">
                                            <SelectItem value="comany">상호명</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <div className="flex items-center border border-[#dddddd] rounded-[5px] bg-white overflow-hidden w-[320px] px-[16px]">
                                        <input
                                            type="text"
                                            value={searchForm.searchtxt}
                                            onChange={e => setSearchForm(prev => ({ ...prev, searchtxt: e.target.value }))}
                                            placeholder="검색어를 입력해주세요."
                                            className={`text-[16px] placeholder:text-[#878A93] w-full focus:outline-none py-[12px]`}
                                        />
                                        <ReadingGlasses />
                                    </div>
                                </div>
                            </form>

                            <div className="mt-[8px] mb-[40px] border-t border-[#dddddd]">
                                <div className="bg-[#DCE1EC] flex justify-between px-[30px] py-[20px] rounded-[5px]">
                                    <div className="flex items-center gap-[30px]">
                                        <label htmlFor="all" className="flex items-center gap-x-2">
                                            <input type="checkbox" id="all" onChange={e => allCheck(e.target.checked)} ref={allCheckRef} />
                                            <span>전체선택</span>
                                        </label>
                                        <label htmlFor="" className="text-[#222222] text-[18px]">
                                            선택한 게시글 총 <span className="text-[#4F46E5]">{selectedRows.length}</span>개
                                        </label>
                                    </div>

                                    <ConfirmDialog
                                        title="요청 삭제"
                                        trigger={open => (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    if (selectedRows.length === 0) {
                                                        toast.error("삭제할 게시글을 선택해주세요.", toastErrorSyle);
                                                        return;
                                                    }
                                                    open();
                                                }}
                                                className="bg-[#FF5463] text-white text-[14px] rounded-[5px] px-[12px] py-[7.5px]"
                                            >
                                                삭제
                                            </button>
                                        )}
                                        handleConfirm={deleteRow}
                                    />
                                </div>

                                <ConsoleTable
                                    thead={[
                                        {
                                            title: "NO",
                                            width: "8%",
                                            key: "index",
                                            content: (row: DataListProps, rowIndex) => {
                                                const index = data!.total_count - (searchForm.page - 1) * 10 - rowIndex;
                                                return (
                                                    <label
                                                        htmlFor={`list-${row.list_no}`}
                                                        className="flex items-center justify-center gap-x-1 px-2 flex-wrap"
                                                        onClick={e => e.stopPropagation()}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            id={`list-${row.list_no}`}
                                                            checked={selectedRows.includes(row.list_no)}
                                                            onChange={() => toggleRow(row.list_no)}
                                                        />
                                                        <p className="break-all text-center">{makeIntComma(index) ?? 0}</p>
                                                    </label>
                                                );
                                            },
                                        },
                                        {
                                            title: "상호명",
                                            width: "19%",
                                            key: "company",
                                            className: twMerge(theadStyle, "text-[#666666]"),
                                            // content(row, rowIndex) {
                                            //     return (
                                            //         <Link to={`${row.list_no}`} className="flex justify-center hover:underline">
                                            //             {row.company}
                                            //         </Link>
                                            //     );
                                            // },
                                        },
                                        {
                                            title: "담당자명",
                                            width: "20%",
                                            key: "name",
                                            className: twMerge(theadStyle, "text-[#666666]"),
                                        },
                                        {
                                            title: "담당자 번호",
                                            width: "16%",
                                            key: "mobile",
                                            className: twMerge(theadStyle, "text-[#666666]"),
                                        },
                                        {
                                            title: "큐알프린트",
                                            width: "12%",
                                            key: "printer",
                                            className: twMerge(theadStyle, "text-[#666666]"),
                                        },
                                        {
                                            title: "등록일",
                                            width: "12%",
                                            key: "w_date",
                                            className: theadStyle,
                                            content: (row: DataListProps) => <p className="text-[#666666]">{row.w_date}</p>,
                                        },
                                        {
                                            title: "접수현황",
                                            width: "16%",
                                            key: "w_date",
                                            className: twMerge(theadStyle, "text-[#666666]"),
                                            content(row, rowIndex) {
                                                return (
                                                    <div className="flex justify-center">
                                                        <Select
                                                            defaultValue={row.result.toString()}
                                                            onValueChange={value => handleChangeRowStatus(row, value)}
                                                        >
                                                            <SelectTrigger
                                                                className={`relative text-[#1B1C1E] text-[16px] flex gap-[10px] rounded-[5px] focus:ring-2  border-none shadow-none max-w-[120px] justify-center ${row.result === 7 ? "text-[#4F46E5]" : "text-[#999999]"}`}
                                                            >
                                                                <SelectValue placeholder="" />
                                                                <ArrowDown className="[&_path]:fill-[#1C1B1F]" />
                                                            </SelectTrigger>
                                                            <SelectContent className="bg-white">
                                                                <SelectItem value="1">접수대기</SelectItem>
                                                                <SelectItem value="2">중복건</SelectItem>
                                                                <SelectItem value="3">진행보류</SelectItem>
                                                                <SelectItem value="4">설치거부</SelectItem>
                                                                <SelectItem value="5">프린터발송</SelectItem>
                                                                <SelectItem value="6">A4라벨지</SelectItem>
                                                                <SelectItem value="7">설치완료</SelectItem>
                                                                <SelectItem value="8">기타</SelectItem>
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                );
                                            },
                                        },
                                        {
                                            title: "메모",
                                            width: "5%",
                                            key: "index",
                                            className: "p-0",
                                            content: (row: DataListProps, rowIndex) => {
                                                return (
                                                    <div className="flex justify-center" onClick={e => e.stopPropagation()}>
                                                        <Popover
                                                            onOpenChange={open => {
                                                                if (open) {
                                                                    setSelectedIdx(row.list_no);
                                                                } else {
                                                                    setMemo("");
                                                                    setSelectedIdx(null);
                                                                }
                                                            }}
                                                        >
                                                            <PopoverTrigger
                                                                asChild
                                                                className="w-[48px] h-[48px] rounded-full hover:bg-black/5 p-3"
                                                                style={{
                                                                    pointerEvents:
                                                                        selectedIdx && selectedIdx !== row.list_no ? "none" : "auto",
                                                                }}
                                                                // onClick={e => {
                                                                //     // 메모가 열린 상태일 때, 현재 열린 메모가 아닌 다른 메모의 클릭 막기
                                                                //     if (selectedIdx && selectedIdx !== row.list_no) {
                                                                //         e.preventDefault();
                                                                //         return;
                                                                //     }
                                                                // }}
                                                            >
                                                                {row.comment ? <MemoOn /> : <Memo />}
                                                            </PopoverTrigger>
                                                            <PopoverContent
                                                                onInteractOutside={event => {
                                                                    event.preventDefault(); // 외부 클릭이 Popover를 닫지 않도록
                                                                }}
                                                                align="end"
                                                                alignOffset={5}
                                                                className="bg-white p-0 border-none rounded-2xl shadow-[0_0_16px_rgba(0,0,0,0.1)] w-[800px]"
                                                            >
                                                                <div className="pb-3 px-5">
                                                                    <div className="">
                                                                        <div className="flex justify-between items-center w-full py-4 ">
                                                                            <p className="text-[20px] text-[#1B1C1E] font-[500]">
                                                                                메모 입력
                                                                            </p>
                                                                            <Close
                                                                                className="cursor-pointer"
                                                                                onClick={() => memoRef.current?.click()}
                                                                            />
                                                                            <PopoverClose ref={memoRef} className="absolute" />
                                                                        </div>
                                                                        <textarea
                                                                            autoFocus
                                                                            value={memo}
                                                                            onChange={e => setMemo(e.target.value)}
                                                                            maxLength={1000}
                                                                            placeholder="메모를 입력해주세요.&#13;&#10;해당 메모는 관리자에게만 보입니다."
                                                                            className="mt-2 focus:border-new focus:border outline-0 text-[16px] min-h-[144px] py-3 px-4 text-[#132E47] border resize-none w-full rounded-lg"
                                                                        />
                                                                        <p className="text-right text-xs text-[#5a5c63] mb-2 mr-1">
                                                                            <span className="text-new">{memo.length.toLocaleString()}</span>{" "}
                                                                            / 1,000
                                                                        </p>
                                                                        <Button
                                                                            onClick={() => handlePostMomo(row.list_no)}
                                                                            size="L"
                                                                            className="w-full border-none bg-[#f3f5f8] text-[#878a93]"
                                                                        >
                                                                            저장
                                                                        </Button>
                                                                    </div>
                                                                    <div className="py-5 border-t mt-5 space-y-2">
                                                                        {detailData?.comment.length === 0 ? (
                                                                            <p className="text-center py-5">등록된 메모가 없습니다.</p>
                                                                        ) : (
                                                                            <>
                                                                                {detailData?.comment.map(item => (
                                                                                    <div key={item.c_list_no} className="flex gap-3">
                                                                                        <b className="text-nowrap min-w-[60px]">
                                                                                            {item.c_name}
                                                                                        </b>
                                                                                        <span style={{ wordBreak: "keep-all" }}>
                                                                                            {item.c_content}
                                                                                        </span>
                                                                                        <span className="text-[#cccccc] text-nowrap">
                                                                                            {item.c_wdate.substring(5, 10) +
                                                                                                " " +
                                                                                                item.c_wdate.substring(11, 16)}
                                                                                        </span>
                                                                                    </div>
                                                                                ))}
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </PopoverContent>
                                                        </Popover>
                                                    </div>
                                                );
                                            },
                                        },
                                    ]}
                                    tbody={data?.data_list || []}
                                    uniqueKeyName="list_no"
                                    isLoading={isLoading}
                                    trClassName="hover:bg-[#f8f8f8] cursor-pointer"
                                    trClick={row => navigate(`${row.list_no}`)}
                                />

                                {/* <div className="flex justify-end mt-5">
                                    <button
                                        className="bg-black text-white rounded-[5px] px-4 py-2"
                                        onClick={() => navigate("create", { state: { page: searchParams.get("page") ?? "1" } })}
                                    >
                                        작성하기
                                    </button>
                                </div> */}
                            </div>

                            <Pagination
                                current={searchForm.page}
                                total={data?.total_count || 1}
                                pageSize={Number(searchForm.limit)}
                                onChange={newPage => {
                                    searchParams.set("page", String(newPage));
                                    setSearchParams(searchParams); // URL 자동 갱신됨
                                    // setSearchForm(prev => ({ ...prev, page: newPage }))
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <Toaster position="bottom-center" reverseOrder={false} />
        </>
    );
}
